import { useState, useMemo } from "react";
import { Calendar as ReactBigCalendar, View } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./overrides.css";
import { sessionsOptions } from "../../screens/sessions/sessionsOptions";
import { parseCalendarEvents } from "../../utils/parseCalendarEvents";
import { CalendarEvent } from "./calendar.types";
import {
    useEventDrop,
    useFormats,
    useHandleSelectSlot,
    localizer,
    scrollToTime,
    handleCalendarDate,
    minTime,
    maxTime,
    handleRanges,
} from "./calendarUtils";
import CustomYearView from "./custom-year-view/CustomYearView";
import { useStore } from "../../store/store";
import { Paper } from "@mui/material";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { SessionPreview } from "../../screens/sessions/components/SessionPreview";
import { CustomEventWeek, CustomEventDay, CustomEventAgenda } from "./CustomEvent";
import { isLightColor } from "../../utils/isLightColor";
import { CalendarView } from "../../store/store.types";
import { OverlayEvent } from "./overlay/overlay.types";
import { useOverlayCalendarBindings } from "./overlay/useOverlayCalendarBindings";

type CalendarProps = {
    items: any;
    refetch: () => void;
    isMobile: boolean;
    views?: {
        year?: boolean;
        month?: boolean;
        week?: boolean;
        day?: boolean;
        agenda?: boolean;
    };
    defaultView: CalendarView;
    height?: number;
    backgroundEvents?: Array<OverlayEvent>;
    showBackgroundEvents?: boolean;
    discountBreaks?: boolean;
};

const DnDCalendar = withDragAndDrop(ReactBigCalendar);

const Calendar = ({
    items,
    refetch,
    views,
    defaultView,
    height,
    backgroundEvents,
    showBackgroundEvents,
    discountBreaks,
}: CalendarProps) => {
    const [event, setEvent] = useState<CalendarEvent>();
    const [previewIsOpen, setPreviewIsOpen] = useState<boolean>(false);
    const {
        calendar: { calendarView, setCalendarView, calendarFilters, setCalendarFilters, scheduleOverlayGhostBookings },
    } = useStore();

    const calendarEvents = useMemo(() => parseCalendarEvents(items), [items]);

    const overlayBindings = useOverlayCalendarBindings({
        calendarEvents,
        backgroundEvents,
        showBackgroundEvents,
        discountBreaks,
        dateRange: calendarFilters?.dateRange,
    });

    const onEventChange = useEventDrop("sessions");
    const { formats } = useFormats("start_date");
    const handleSelectSlot = useHandleSelectSlot();

    return (
        <Paper className="sessions-calendar-wrapper" sx={{ p: 2, height: height }}>
            <DnDCalendar
                localizer={localizer}
                culture="pt-PT"
                views={
                    {
                        month: views?.month ?? false,
                        week: views?.week ?? false,
                        day: views?.day ?? false,
                        agenda: views?.agenda ?? false,
                        year: views?.year ? CustomYearView : false,
                    } as any
                }
                messages={{ year: "Year" } as any}
                components={{
                    toolbar: overlayBindings.toolbarRenderer,
                    week: {
                        // @ts-ignore
                        event: CustomEventWeek,
                        header: overlayBindings.dayHeaderRenderer,
                    },
                    day: {
                        // @ts-ignore
                        event: CustomEventDay,
                        header: overlayBindings.dayHeaderRenderer,
                    },
                    agenda: {
                        // @ts-ignore
                        event: CustomEventAgenda,
                        title: () => <h1>cenas</h1>,
                    },
                }}
                events={calendarEvents}
                // @ts-ignore
                backgroundEvents={overlayBindings.effectiveBackgroundEvents}
                // @ts-ignore
                startAccessor="start"
                // @ts-ignore
                endAccessor="end"
                dayLayoutAlgorithm="no-overlap"
                //@ts-ignore
                defaultView={calendarView && views?.[calendarView] ? calendarView : defaultView}
                style={{ height: "calc(100vh - 150px)", position: "relative" }}
                min={minTime}
                max={maxTime}
                timeslots={4}
                step={15}
                scrollToTime={scrollToTime}
                date={calendarFilters?.dateRange && handleCalendarDate(calendarFilters.dateRange)}
                // @ts-ignore
                onSelectEvent={(event: CalendarEvent) => {
                    setEvent(event);
                    setPreviewIsOpen(true);
                }}
                selectable={true}
                onSelectSlot={(e) =>
                    handleSelectSlot({
                        start: e.start,
                        entity: "sessions",
                        defaultMinutes: 60,
                    })
                }
                onView={(view: View) => {
                    setCalendarView(view as CalendarView);
                }}
                onNavigate={(date) => {}}
                onRangeChange={(range: any, view: View | undefined) => {
                    const effectiveView = view || calendarView;
                    const newFilters = {
                        ...calendarFilters,
                        view: effectiveView as CalendarView,
                        dateRange: handleRanges(range),
                    };
                    setCalendarFilters(newFilters);
                }}
                eventPropGetter={overlayBindings.composeEventPropGetter(
                    buildGhostableSessionEventStyle(scheduleOverlayGhostBookings && !!showBackgroundEvents)
                )}
                // @ts-ignore
                formats={formats}
                draggableAccessor={(event) => true}
                onEventDrop={onEventChange}
            />

            <SessionPreview
                previewIsOpen={previewIsOpen}
                setPreviewIsOpen={setPreviewIsOpen}
                event={event}
                groupsOptions={sessionsOptions}
                refetchSessions={refetch}
            />
        </Paper>
    );
};

const GHOST_BOOKINGS_OPACITY = 0.25;

const buildGhostableSessionEventStyle = (ghostBookings: boolean) => {
    const styleGetter = (event: any) => {
        const result: any = buildSessionEventStyle(event);
        if (ghostBookings) {
            result.style.opacity = GHOST_BOOKINGS_OPACITY;
            result.className = "ghost-booking";
        }
        return result;
    };
    return styleGetter;
};

const buildSessionEventStyle = (event: any) => {
    const defaultColor = "#d1cfcf";
    const eventColor = event?.color ? event.color : defaultColor;

    const style: any = {
        backgroundColor: eventColor,
        borderColor: eventColor,
        color: isLightColor(eventColor) ? "#404040" : "#fff",
        fontSize: "11px",
    };

    applyStyleIfPastDue(event, style);

    const result = { style };
    return result;
};

const applyStyleIfPastDue = (event: any, style: any) => {
    const eventEndDate = new Date(event.end);
    const now = new Date();
    if (
        eventEndDate < now &&
        event.status !== "done" &&
        event.status !== "cancelled" &&
        event.status !== "missed" &&
        event.status !== "staff_unavailable" &&
        event.service !== null
    ) {
        style.boxShadow = "inset 0px 0px 0px 4px red";
        style.animation = "blink 1s linear infinite";
    }
    return false;
};

export default Calendar;
